import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Container, Typography, Paper, Button, Stack, Box, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  TextField, Tabs, Tab, Card, CardContent, Divider, FormControl, InputLabel,
  Select, MenuItem
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { Add, Edit, Delete, Settings } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL;

export default function Configuracoes() {
  const [tabIndex, setTabIndex] = useState(0);

  // --- Usuários ---
  const [usuarios, setUsuarios] = useState([]);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [openUsuarioDialog, setOpenUsuarioDialog] = useState(false);
  const [usuarioAtual, setUsuarioAtual] = useState({ id: null, nome: '', usuario: '', senha: '', permissao: '' });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ========== USUÁRIOS ==========
  const carregarUsuarios = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/usuarios`, { headers: { Authorization: `Bearer ${token}` } });
      setUsuarios(data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao carregar usuários', severity: 'error' });
    }
  }, []);

  const handleOpenUsuarioDialog = (usuario = null) => {
    if (usuario) {
      setUsuarioAtual(usuario);
      setModoEdicao(true);
    } else {
      setUsuarioAtual({ id: null, nome: '', usuario: '', senha: '', permissao: '' });
      setModoEdicao(false);
    }
    setOpenUsuarioDialog(true);
  };

  const handleCloseUsuarioDialog = () => {
    setOpenUsuarioDialog(false);
    setUsuarioAtual({ id: null, nome: '', usuario: '', senha: '', permissao: '' });
    setModoEdicao(false);
  };

  const handleSalvarUsuario = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (modoEdicao) {
        await axios.put(`${API_URL}/trocar-senha/${usuarioAtual.id}`, { senha: usuarioAtual.senha }, config);
        setSnackbar({ open: true, message: 'Senha atualizada com sucesso', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/cadastrar-usuario`, { nome: usuarioAtual.nome, usuario: usuarioAtual.usuario, senha: usuarioAtual.senha, permissao: usuarioAtual.permissao }, config);
        setSnackbar({ open: true, message: 'Usuário criado com sucesso', severity: 'success' });
      }
      handleCloseUsuarioDialog();
      carregarUsuarios();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.mensagem || 'Erro ao salvar usuário', severity: 'error' });
    }
  };

  const handleExcluirUsuario = useCallback(async (id) => {
    if (!window.confirm('Deseja realmente excluir este usuário?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/excluir-usuario/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSnackbar({ open: true, message: 'Usuário excluído com sucesso', severity: 'success' });
      carregarUsuarios();
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao excluir usuário', severity: 'error' });
    }
  }, [carregarUsuarios]);

  // ========== EFFECTS ==========
  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  // ========== COLUNAS ==========
  const colunasUsuarios = useMemo(() => [
    { accessorKey: 'id', header: 'ID', size: 80 },
    { accessorKey: 'nome', header: 'Nome', size: 200 },
    { accessorKey: 'usuario', header: 'Usuário', size: 200 },
    { accessorKey: 'permissao', header: 'Permissão', size: 150, Cell: ({ cell }) => cell.getValue() === 'admin' ? 'Admin' : 'Funcionário' },
    {
      accessorKey: 'acoes', header: 'Ações', size: 120,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => handleOpenUsuarioDialog(row.original)}><Edit fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => handleExcluirUsuario(row.original.id)}><Delete fontSize="small" /></IconButton></Tooltip>
        </Stack>
      )
    }
  ], [handleExcluirUsuario]);

  const tabelaProps = {
    localization: MRT_Localization_PT_BR,
    enablePagination: true,
    enableSorting: true,
    enableColumnFilters: true,
    enableDensityToggle: true,
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTableProps: { sx: { tableLayout: 'fixed' } }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Settings sx={{ fontSize: 40, color: '#ED3237' }} />
        <Box>
          <Typography variant="h4" fontWeight={700}>Configurações do Sistema</Typography>
          <Typography variant="body2" color="text.secondary">Gerencie usuários e configurações gerais</Typography>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab label="Usuários" />
          <Tab label="Geral" />
        </Tabs>
      </Paper>

      {/* ---- USUÁRIOS ---- */}
      {tabIndex === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Usuários do Sistema</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenUsuarioDialog()} sx={{ bgcolor: '#ED3237', '&:hover': { bgcolor: '#e67e00' } }}>
              Novo Usuário
            </Button>
          </Box>
          <Paper sx={{ p: 2 }}>
            <MaterialReactTable columns={colunasUsuarios} data={usuarios} {...tabelaProps} />
          </Paper>
        </>
      )}

      {/* ---- GERAL ---- */}
      {tabIndex === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>Configurações Gerais</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">Configurações gerais do sistema em desenvolvimento.</Typography>
          </CardContent>
        </Card>
      )}

      {/* ---- DIALOG USUÁRIO ---- */}
      <Dialog open={openUsuarioDialog} onClose={handleCloseUsuarioDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{modoEdicao ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField name="nome" label="Nome" value={usuarioAtual.nome} onChange={e => setUsuarioAtual({ ...usuarioAtual, nome: e.target.value })} fullWidth required autoFocus disabled={modoEdicao} />
            <TextField name="usuario" label="Usuário" value={usuarioAtual.usuario} onChange={e => setUsuarioAtual({ ...usuarioAtual, usuario: e.target.value })} fullWidth required disabled={modoEdicao} />
            <TextField name="senha" label={modoEdicao ? 'Nova Senha' : 'Senha'} type="password" value={usuarioAtual.senha} onChange={e => setUsuarioAtual({ ...usuarioAtual, senha: e.target.value })} fullWidth required />
            {!modoEdicao && (
              <FormControl fullWidth>
                <InputLabel>Permissão</InputLabel>
                <Select name="permissao" value={usuarioAtual.permissao} onChange={e => setUsuarioAtual({ ...usuarioAtual, permissao: e.target.value })} label="Permissão">
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="funcionario">Funcionário</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUsuarioDialog}>Cancelar</Button>
          <Button onClick={handleSalvarUsuario} variant="contained" sx={{ bgcolor: '#ED3237', '&:hover': { bgcolor: '#e67e00' } }}>{modoEdicao ? 'Atualizar Senha' : 'Criar'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}
